/**
 * The in-room presence shim (gateway-flow-c-design.md §4.2/§4.5) — pings a heartbeat
 * endpoint every `intervalMs` while the tab is VISIBLE and the browser reports itself
 * ONLINE, so a backgrounded or offline tab cannot fabricate presence. The payload sent
 * to the server is never a timestamp — this hook has no time field to send even by
 * mistake, because it never touches `Date` for anything the server sees; every `at` in
 * the billable clock is stamped server-side.
 *
 * Both roles heartbeat while `ConsultRoomFrame` is mounted (gateway-api-contract.md §2's
 * "Heartbeat, both roles" amendment — nothing on the practitioner side ever heartbeated
 * before this, which is half of why a consult could bill zero): the patient posts
 * `POST /api/consults/{id}/heartbeat` with body `{token}` (`gatewayApi.heartbeat`); the
 * practitioner posts the same path with an empty body under their owner session
 * (`gatewayApi.practitionerHeartbeat`), identified by role rather than payload. `role`
 * defaults from whether a `token` was supplied, matching every other Flow C entry point.
 *
 * Silent by design: a missed beat is recovered by the server's grace-period bridge, so
 * `lastError` is exposed for diagnostics only, never as a blocking UI state. Silent is NOT
 * the same as stopped, though — see the in-flight deadline below. A hook that goes quiet
 * for the rest of a consultation is a bill computed from one instant.
 *
 * `gatewayApi` is an optional trailing param defaulting to the module singleton,
 * mirroring the component-level DI rule (contract §9).
 */
import { useEffect, useRef, useState } from "react";
import defaultGatewayApi from "../api";

export function useConsultHeartbeat(
  appointmentId,
  token,
  { intervalMs = 20000, role = token ? "patient" : "practitioner" } = {},
  gatewayApi = defaultGatewayApi,
) {
  const [lastSentAt, setLastSentAt] = useState(null);
  const [lastError, setLastError] = useState(null);
  // The instant the in-flight request STARTED, not a boolean. A plain flag cleared in a
  // `finally` is correct for a request that resolves or rejects and useless for one that
  // does neither: a socket that never settles left the flag raised for ever, every
  // subsequent tick returned at the guard, and presence stopped permanently with no
  // error and nothing on screen. Presence IS the bill — with the terminal taken as the
  // EARLIER of the two roles' last-seen, one party going silent pins the whole span to
  // their last successful beat, and a delivered consult can compute to zero minutes.
  const inFlightSinceRef = useRef(null);

  useEffect(() => {
    if (!appointmentId) return undefined;
    if (role === "patient" && !token) return undefined;
    let stopped = false;

    const canSend = () =>
      typeof document !== "undefined"
      && document.visibilityState === "visible"
      && (typeof navigator === "undefined" || navigator.onLine !== false);

    const send = async () => {
      if (stopped || !canSend()) return;
      // BOUNDED, not abandoned. The guard still suppresses a second beat while one is
      // genuinely in flight — that is what stops a slow server being hammered — but it
      // expires after one interval, so a hung request costs at most one missed beat
      // instead of every future one. Overlapping heartbeats are harmless: the server
      // `$max`es the presence stamp, so a duplicate can only ever confirm the same fact.
      const since = inFlightSinceRef.current;
      if (since !== null && Date.now() - since < intervalMs) return;
      inFlightSinceRef.current = Date.now();
      try {
        // Patient: exactly {token} via `heartbeat`. Practitioner: an empty body via
        // `practitionerHeartbeat`, role decided by the owner session — see module
        // docstring. Never add a second argument to either call.
        if (role === "practitioner") {
          await gatewayApi.practitionerHeartbeat(appointmentId);
        } else {
          await gatewayApi.heartbeat(appointmentId, token);
        }
        if (!stopped) {
          setLastSentAt(new Date().toISOString());
          setLastError(null);
        }
      } catch (e) {
        if (!stopped) setLastError(e);
      } finally {
        inFlightSinceRef.current = null;
      }
    };

    // A tab returning to the foreground (or back online) should not wait a full
    // interval to prove presence again — the gap itself is what the server's
    // grace-period bridge exists to absorb, but the sooner presence resumes, the
    // smaller that gap is.
    const onResume = () => { if (canSend()) send(); };

    const timer = setInterval(send, intervalMs);
    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("online", onResume);
    send();

    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("online", onResume);
    };
  }, [appointmentId, token, intervalMs, role, gatewayApi]);

  return { lastSentAt, lastError };
}

export default useConsultHeartbeat;
